/**
 * Referral code utilities
 * Handles storing and retrieving referral codes from cookies
 */

const REFERRAL_COOKIE_NAME = 'xrpl_referral_code';
const REFERRAL_COOKIE_DAYS = 30; // Cookie expires in 30 days

/**
 * Set a cookie with the given name, value, and expiration days
 */
function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(nameEQ)) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Delete a cookie by name
 */
function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

/**
 * Store a referral code in a cookie for 30 days
 */
export function storeReferralCode(code: string): void {
  if (code && code.trim()) {
    setCookie(REFERRAL_COOKIE_NAME, code.trim().toUpperCase(), REFERRAL_COOKIE_DAYS);
  }
}

/**
 * Get the stored referral code from cookies
 */
export function getReferralCode(): string | null {
  return getCookie(REFERRAL_COOKIE_NAME);
}

/**
 * Clear the stored referral code (e.g., after successful signup)
 */
export function clearReferralCode(): void {
  deleteCookie(REFERRAL_COOKIE_NAME);
}

/**
 * Check URL for referral code parameter and store it if present
 * Should be called on app initialization
 */
export function captureReferralFromUrl(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');

  if (refCode) {
    storeReferralCode(refCode);

    // Optionally, clean up the URL without causing a page reload
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Build a shareable URL with the user's referral code appended
 * @param url - The base URL to share (defaults to current page URL)
 * @param referralCode - The user's referral code (optional)
 * @returns The URL with referral code appended if provided
 */
export function buildShareableUrl(url?: string, referralCode?: string): string {
  const baseUrl = url || window.location.href;

  if (!referralCode) {
    return baseUrl;
  }

  const urlObj = new URL(baseUrl);
  urlObj.searchParams.set('ref', referralCode);
  return urlObj.toString();
}
